import React, { useState } from "react"
import { graphql } from "gatsby"
import { Container, Row, Col, Image, Modal } from "react-bootstrap"
import { PrismicRichText, PrismicLink } from "@prismicio/react"
import { FaPlayCircle } from "react-icons/fa" // Import a play icon from react-icons or use any play icon SVG

const getYouTubeVideoId = (url) => {
  if (!url) return null
  // Handle youtu.be/ID short links
  const shortMatch = url.match(/youtu\.be\/([^?&/]+)/)
  if (shortMatch) return shortMatch[1]
  // Handle youtube.com/watch?v=ID
  const watchMatch = url.match(/[?&]v=([^?&]+)/)
  if (watchMatch) return watchMatch[1]
  // Handle youtube.com/embed/ID
  const embedMatch = url.match(/\/embed\/([^?&/]+)/)
  if (embedMatch) return embedMatch[1]
  // Strip any query params and return as plain ID
  return url.split("?")[0].split("&")[0]
}

export const CtaWithLeftImage = ({ slice }) => {
  const [showModal, setShowModal] = useState(false)

  const handleOpenModal = () => setShowModal(true)
  const handleCloseModal = () => setShowModal(false)

  const videoId = getYouTubeVideoId(slice.primary.youtube_link)

  return (
    <div className={`cta_with_left_image style_number_${slice.primary.style_number}`}>
      {slice.primary.style_number === "0" ? null : (
        <section className="bg-c-7 py-st-2">
          <Container className={slice.primary.style_number === "2" ? "" : "mt-5"}>
            <Row className={slice.primary.style_number === "2" ? "justify-content-center align-items-center" : ""}>
              <Col
                className={(() => {
                  switch (slice.primary.style_number) {
                    case "1":
                      return "px-0 col-lg-6 col-sm-12 col-12 h-min-400px"
                    case "2":
                      return "col-12 col-md-9 col-lg-6 px-0 filter-st-1"
                    default:
                      return "px-0 col-lg-6 col-sm-12 col-12 h-min-400px"
                  }
                })()}
              >
                <div
                  className={(() => {
                    switch (slice.primary.style_number) {
                      case "1":
                        return "position-relative h-100 bg-c-14"
                      default:
                        return ""
                    }
                  })()}
                  style={(() => {
                    switch (slice.primary.style_number) {
                      case "2":
                        return {
                          position: "relative",
                          cursor: videoId ? "pointer" : "default",
                          borderRadius: "1.5rem",
                          overflow: "hidden",
                          aspectRatio: "3 / 2",
                          maxHeight: "440px",
                        }
                      default:
                        return { position: "relative", cursor: "pointer" }
                    }
                  })()}
                  onClick={videoId ? handleOpenModal : null}
                >
                  <Image
                    fluid={slice.primary.style_number !== "2"}
                    className={(() => {
                      switch (slice.primary.style_number) {
                        case "1":
                          return "position-absolute start-0 bottom-0 end-0 m-auto img-st-9 filter-st-1"
                        case "2":
                          return ""
                        default:
                          return ""
                      }
                    })()}
                    style={slice.primary.style_number === "2" ? { width: "100%", height: "100%", objectFit: "cover", display: "block" } : undefined}
                    src={slice.primary.image.url}
                    alt={slice.primary.image.alt}
                  />

                  {/* Conditionally render play icon overlay */}
                  {videoId && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        fontSize: "3rem", // Adjust size of play icon
                        color: "white",
                        opacity: 0.8,
                      }}
                    >
                      <FaPlayCircle />
                    </div>
                  )}
                </div>
              </Col>
              <Col
                className={(() => {
                  switch (slice.primary.style_number) {
                    case "2":
                      return "col-12 col-md-9 col-lg-6 m-auto py-2 px-5"
                    default:
                      return "m-auto px-5 col-lg-6 col-md-12 col-12"
                  }
                })()}
              >
                <PrismicRichText
                  field={slice.primary.title.richText}
                  components={{
                    heading1: ({ children }) => <h1 className="heading-st fs-st-6 py-3">{children}</h1>,
                  }}
                />
                <PrismicRichText
                  field={slice.primary.subtitle.richText}
                  components={{
                    paragraph: ({ children }) => <p className="paragraph-st fs-st-11 py-3">{children}</p>,
                  }}
                />
                <div className="d-flex justify-content-center pt-2 pb-4">
                  {slice.primary.button_link && (
                    <PrismicLink field={slice.primary.button_link} className="btn-st">
                      {slice.primary.button_label}
                    </PrismicLink>
                  )}
                </div>
              </Col>
            </Row>
          </Container>

          {/* Modal for YouTube Video */}
          <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
            <Modal.Body className="p-0 d-flex justify-content-center align-items-center">
              {showModal && videoId && (
                <iframe
                  className="embed-responsive-item"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  allowFullScreen
                  allow="autoplay; encrypted-media"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="YouTube Video"
                  style={{
                    minWidth: "100%",
                    minHeight: "500px",
                    border: "none"
                  }}
                />
              )}
            </Modal.Body>
          </Modal>
        </section>
      )}
    </div>
  )
}

export const query = graphql`
  fragment HomepageDataBodyCtaWithLeftImage on PrismicHomepageDataBodyCtaWithLeftImage {
    id
    slice_type
    primary {
      style_number
      image {
        gatsbyImageData
        alt
        url
      }
      subtitle {
        richText
        text
      }
      title {
        richText
        text
      }
      button_label
      button_link {
        url
        target
        link_type
      }
      youtube_link
    }
  }

  fragment PageDataBodyCtaWithLeftImage on PrismicPageDataBodyCtaWithLeftImage {
    id
    slice_type
    primary {
      style_number
      image {
        gatsbyImageData
        alt
        url
      }
      subtitle {
        richText
        text
      }
      title {
        richText
        text
      }
      button_label
      button_link {
        url
        target
        link_type
      }
      youtube_link
    }
  }
`
